import { useRef, useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ImagePlus, Download, ShieldCheck, X, Image as ImageIcon, RotateCcw } from "lucide-react";

const FORMAT_OPTIONS = [
  { id: "image/jpeg", ext: "jpg", label: "JPG" },
  { id: "image/png", ext: "png", label: "PNG" },
  { id: "image/webp", ext: "webp", label: "WEBP" },
];

const MODE_OPTIONS = [
  { id: "quality", label: "Quality slider" },
  { id: "targetSize", label: "Fit under target size" },
];

function prettySize(bytes) {
  if (bytes == null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

function baseName(filename) {
  const dot = filename.lastIndexOf(".");
  return dot === -1 ? filename : filename.substring(0, dot);
}

export default function ImageEditorPage() {
  const fileInputRef = useRef(null);
  const [original, setOriginal] = useState(null); // { file, url, width, height }
  const [format, setFormat] = useState("image/jpeg");
  const [mode, setMode] = useState("quality");
  const [quality, setQuality] = useState(0.8); // 0.1–1.0
  const [targetKb, setTargetKb] = useState(500);
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [width, setWidth] = useState("");
  const [height, setHeight] = useState("");
  const [lockAspect, setLockAspect] = useState(true);
  const [output, setOutput] = useState(null); // { blob, url, size, width, height }
  const [busy, setBusy] = useState(false);
  const dropRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    return () => {
      if (original?.url) URL.revokeObjectURL(original.url);
      if (output?.url) URL.revokeObjectURL(output.url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Whenever inputs change, invalidate the previous output
    if (output) {
      URL.revokeObjectURL(output.url);
      setOutput(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [format, mode, quality, targetKb, resizeEnabled, width, height]);

  function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (original?.url) URL.revokeObjectURL(original.url);
    if (output?.url) URL.revokeObjectURL(output.url);
    setOutput(null);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setOriginal({ file, url, width: img.width, height: img.height });
      setWidth(String(img.width));
      setHeight(String(img.height));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error("Could not read this image");
    };
    img.src = url;
  }

  function onWidthChange(v) {
    setWidth(v);
    if (lockAspect && original && v) {
      const ratio = original.height / original.width;
      setHeight(String(Math.round(Number(v) * ratio)));
    }
  }
  function onHeightChange(v) {
    setHeight(v);
    if (lockAspect && original && v) {
      const ratio = original.width / original.height;
      setWidth(String(Math.round(Number(v) * ratio)));
    }
  }

  async function loadBitmap(srcUrl) {
    const img = new window.Image();
    img.decoding = "async";
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("Could not decode image — try a different file"));
      img.src = srcUrl;
    });
    return img;
  }

  async function renderToBlob(img, outW, outH, outFormat, q) {
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d");
    if (outFormat === "image/jpeg") {
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, outW, outH);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(img, 0, 0, outW, outH);
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((b) => b ? resolve(b) : reject(new Error("Encode failed — try a different format")), outFormat, q);
    });
    return blob;
  }

  async function process() {
    if (!original) { toast.error("Pick an image first"); return; }
    setBusy(true);
    try {
      // Reuse the preview blob URL — already successfully decoded and displayed.
      const img = await loadBitmap(original.url);
      let outW = img.naturalWidth, outH = img.naturalHeight;
      if (resizeEnabled && width && height) {
        outW = Math.max(1, Math.round(Number(width)));
        outH = Math.max(1, Math.round(Number(height)));
      }
      let blob;
      if (mode === "targetSize") {
        const targetBytes = Math.max(1024, Number(targetKb) * 1024);
        if (format === "image/png") {
          let w = outW, h = outH;
          for (let i = 0; i < 8; i++) {
            blob = await renderToBlob(img, w, h, format, undefined);
            if (blob.size <= targetBytes) break;
            w = Math.max(1, Math.round(w * 0.85));
            h = Math.max(1, Math.round(h * 0.85));
          }
        } else {
          let lo = 0.1, hi = 0.95, best = null;
          for (let i = 0; i < 8; i++) {
            const mid = (lo + hi) / 2;
            const b = await renderToBlob(img, outW, outH, format, mid);
            if (b.size <= targetBytes) { best = b; lo = mid; } else { hi = mid; }
          }
          blob = best || await renderToBlob(img, outW, outH, format, lo);
          if (blob.size > targetBytes) {
            let w = outW, h = outH, q = lo;
            for (let i = 0; i < 6 && blob.size > targetBytes; i++) {
              w = Math.max(1, Math.round(w * 0.85));
              h = Math.max(1, Math.round(h * 0.85));
              blob = await renderToBlob(img, w, h, format, q);
            }
          }
        }
      } else {
        const q = format === "image/png" ? undefined : quality;
        blob = await renderToBlob(img, outW, outH, format, q);
      }
      const outUrl = URL.createObjectURL(blob);
      const outImg = new window.Image();
      await new Promise((resolve) => {
        outImg.onload = resolve;
        outImg.onerror = resolve;
        outImg.src = outUrl;
      });
      setOutput({ blob, url: outUrl, size: blob.size, width: outImg.naturalWidth, height: outImg.naturalHeight });
      toast.success("Processed");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Could not process image");
    } finally {
      setBusy(false);
    }
  }

  function download() {
    if (!output) return;
    const ext = FORMAT_OPTIONS.find(f => f.id === format)?.ext || "jpg";
    const name = `${baseName(original.file.name)}-handy.${ext}`;
    const a = document.createElement("a");
    a.href = output.url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  function reset() {
    if (original?.url) URL.revokeObjectURL(original.url);
    if (output?.url) URL.revokeObjectURL(output.url);
    setOriginal(null);
    setOutput(null);
    setWidth("");
    setHeight("");
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  }

  const ratioPct = original && output ? Math.round((1 - output.size / original.file.size) * 100) : null;
  const ratioLabel = ratioPct == null ? "" : (ratioPct > 0 ? ` · -${ratioPct}%` : ratioPct < 0 ? ` · +${Math.abs(ratioPct)}%` : " · same size");

  return (
    <div className="animate-fade-up max-w-4xl">
      <div className="mb-6">
        <span className="label-caps">Tool</span>
        <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Image editor.</h1>
        <p className="mt-2 text-[color:var(--text-muted)]">Convert, compress, and resize images.</p>
        <div
          className="mt-4 inline-flex items-center gap-2 text-xs rounded-full border px-3 py-1.5"
          style={{ background: "color-mix(in srgb, var(--primary) 7%, transparent)", borderColor: "color-mix(in srgb, var(--primary) 25%, var(--border-subtle))" }}
          data-testid="image-editor-privacy-badge"
        >
          <ShieldCheck className="h-3.5 w-3.5" style={{ color: "var(--primary)" }} />
          <span style={{ color: "var(--text-muted)" }}>Runs 100% in your browser · no uploads</span>
        </div>
      </div>

      {!original ? (
        <div
          ref={dropRef}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`card-tactile rounded-2xl p-10 border-2 border-dashed cursor-pointer transition-colors ${dragOver ? "border-[color:var(--primary)] bg-[color:var(--surface-2)]" : "border-[color:var(--border-subtle)]"}`}
          data-testid="image-editor-dropzone"
        >
          <div className="flex flex-col items-center text-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-[color:var(--primary-soft)] flex items-center justify-center">
              <ImagePlus className="h-6 w-6 text-[color:var(--primary-ink)]" />
            </div>
            <div>
              <div className="font-display font-bold text-lg">Drop an image here, or click to choose</div>
              <div className="text-sm text-[color:var(--text-muted)] mt-1">PNG · JPG · WEBP · HEIC · Up to ~50 MB</div>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="image-editor-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="card-tactile rounded-2xl p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="label-caps">Original</div>
                <span className="text-xs text-[color:var(--text-muted)]" data-testid="image-editor-original-size">
                  {original.width}×{original.height} · {prettySize(original.file.size)}
                </span>
              </div>
              <img src={original.url} alt="original" className="w-full max-h-[320px] object-contain rounded-lg bg-[color:var(--surface-2)]" />
            </div>
            <div className="card-tactile rounded-2xl p-4">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="label-caps">Output</div>
                <span className="text-xs text-[color:var(--text-muted)]" data-testid="image-editor-output-size">
                  {output ? `${output.width}×${output.height} · ${prettySize(output.size)}${ratioLabel}` : "Not processed yet"}
                </span>
              </div>
              {output ? (
                <img src={output.url} alt="output" className="w-full max-h-[320px] object-contain rounded-lg bg-[color:var(--surface-2)]" />
              ) : (
                <div className="w-full h-[220px] rounded-lg bg-[color:var(--surface-2)] flex items-center justify-center text-[color:var(--text-muted)] text-sm">
                  <ImageIcon className="h-4 w-4 mr-2" /> Set options below and hit Process
                </div>
              )}
            </div>
          </div>

          <div className="card-tactile rounded-2xl p-6 space-y-5">
            <div>
              <label className="label-caps">Output format</label>
              <div className="mt-2 flex gap-2" data-testid="image-editor-format-row">
                {FORMAT_OPTIONS.map(f => (
                  <button
                    key={f.id}
                    onClick={() => setFormat(f.id)}
                    data-testid={`image-editor-format-${f.ext}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${format === f.id ? "bg-[color:var(--primary)] text-primary-foreground border-transparent" : "bg-transparent text-[color:var(--text-main)] border-[color:var(--border-subtle)] hover:border-[color:var(--primary)]"}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="label-caps">Compression mode</label>
              <div className="mt-2 flex gap-2" data-testid="image-editor-mode-row">
                {MODE_OPTIONS.map(m => (
                  <button
                    key={m.id}
                    onClick={() => setMode(m.id)}
                    data-testid={`image-editor-mode-${m.id}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-medium border transition-colors ${mode === m.id ? "bg-[color:var(--primary)] text-primary-foreground border-transparent" : "bg-transparent text-[color:var(--text-main)] border-[color:var(--border-subtle)] hover:border-[color:var(--primary)]"}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {mode === "quality" ? (
                <div className="mt-3">
                  <label className="text-xs text-[color:var(--text-muted)]">Quality: {Math.round(quality * 100)}%</label>
                  <input
                    type="range" min="0.1" max="1" step="0.05"
                    value={quality}
                    onChange={(e) => setQuality(Number(e.target.value))}
                    data-testid="image-editor-quality"
                    className="mt-1 w-full accent-[color:var(--primary)]"
                  />
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2">
                  <label className="text-xs text-[color:var(--text-muted)]">Target size (KB):</label>
                  <Input
                    type="number" min="10" max="50000"
                    value={targetKb}
                    onChange={(e) => setTargetKb(Math.max(10, Number(e.target.value) || 10))}
                    data-testid="image-editor-target-kb"
                    className="max-w-[140px]"
                  />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="label-caps">Resize dimensions</label>
                <div className="flex items-center gap-2">
                  <span
                    data-testid="image-editor-resize-state"
                    className={`text-[11px] font-semibold uppercase tracking-wider transition-colors ${resizeEnabled ? "text-[color:var(--primary)]" : "text-[color:var(--text-muted)]"}`}
                  >
                    {resizeEnabled ? "On" : "Off"}
                  </span>
                  <Switch
                    checked={resizeEnabled}
                    onCheckedChange={setResizeEnabled}
                    data-testid="image-editor-resize-enabled"
                    className="data-[state=checked]:bg-[color:var(--primary)] data-[state=unchecked]:bg-[color:var(--border-subtle)] border-[color:var(--border-subtle)]"
                  />
                </div>
              </div>
              {resizeEnabled && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <div>
                    <label className="text-xs text-[color:var(--text-muted)]">Width (px)</label>
                    <Input type="number" min="1" value={width} onChange={(e) => onWidthChange(e.target.value)} data-testid="image-editor-width" className="mt-1 max-w-[120px]" />
                  </div>
                  <div>
                    <label className="text-xs text-[color:var(--text-muted)]">Height (px)</label>
                    <Input type="number" min="1" value={height} onChange={(e) => onHeightChange(e.target.value)} data-testid="image-editor-height" className="mt-1 max-w-[120px]" />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={lockAspect}
                      onChange={(e) => setLockAspect(e.target.checked)}
                      data-testid="image-editor-lock-aspect"
                      className="accent-[color:var(--primary)]"
                    />
                    Lock aspect ratio
                  </label>
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2">
              <Button
                onClick={process}
                disabled={busy}
                data-testid="image-editor-process"
                className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-primary-foreground disabled:opacity-50"
              >
                {busy ? "Processing…" : "Process"}
              </Button>
              <Button
                onClick={download}
                disabled={!output}
                data-testid="image-editor-download"
                className="bg-[color:var(--text-main)] text-[color:var(--surface)] hover:bg-[color:var(--text-main)] hover:opacity-90 border border-[color:var(--text-main)] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" /> Download
              </Button>
              <Button
                onClick={reset}
                variant="ghost"
                data-testid="image-editor-reset"
                className="text-[color:var(--text-muted)] hover:text-[color:var(--text-main)] hover:bg-[color:var(--surface-2)] ml-auto"
              >
                <X className="h-4 w-4 mr-1" /> Reset
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
